import { useEffect, useRef, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { filtersToQuery, URL_SYNC_STAMP_KEY, useFilters } from './filters';
import { useSettingsStore } from '@/api/bi/settings';
import { storage } from '@/lib/storage';

function stampUrl(serialized: string): void {
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(URL_SYNC_STAMP_KEY, serialized);
    }
  } catch {
    /* private mode etc */
  }
}

/**
 * Two-way bridge between the filters store and the URL.
 *
 *   Boot:                 read `?range&store&compare&from&to` → store
 *   On in-app change:     write store → URL (replace, no extra history entry)
 *   On back/forward:      read new URL → store
 *
 * Mounting this component once near the router root is sufficient for the
 * whole app: every page reads the store via `useFilters()` and stays in sync.
 */
export function FilterUrlSync({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const hydrate = useFilters((s) => s.hydrateFromQuery);
  const range = useFilters((s) => s.range);
  const storeIds = useFilters((s) => s.storeIds);
  const compareMode = useFilters((s) => s.compareMode);
  const timeRangeMode = useFilters((s) => s.timeRangeMode);
  // The user's saved default range (server-backed → cache fallback). When
  // the live range equals it we strip `range=` from the URL so reloads
  // naturally fall back to the default — including after the user changes
  // their default in Settings (the stale `range=PREV` no longer wins).
  const savedDefault = useSettingsStore(
    (s) => s.blob.prefs?.default_range ?? null,
  );
  const lastWrittenRef = useRef<string>('');
  // Cheap stable signature so we don't churn URLs when the array reference
  // changes but the contents don't (zustand selectors return new arrays
  // every set even when membership is identical).
  const storeKey = storeIds.join(',');

  // Initial hydrate + react to back/forward.
  useEffect(() => {
    hydrate(location.search);
    // We deliberately omit `hydrate` from deps so it only runs on URL changes;
    // the store action identity is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Push store changes back to the URL (replace so we don't pollute history).
  useEffect(() => {
    // Resolve the saved default at write time (the settings store hydrates
    // asynchronously, so the value we close over above might be `null` on
    // the first paint — fall back to the local cache mirror).
    const effectiveDefault =
      savedDefault ?? storage.getPrefs().default_range ?? null;
    const next = filtersToQuery({ range, storeIds, compareMode, timeRangeMode });
    const current = new URLSearchParams(location.search);
    // Preserve unrelated params (e.g. drilldown ids, tab=, etc.).
    const incoming = new URLSearchParams(next);
    incoming.forEach((v, k) => current.set(k, v));
    // When the live range is the user's saved default *preset*, drop `range`
    // (and any leftover `from`/`to`) from the URL so cold-boots fall through
    // to the default and changing the default in Settings takes effect on
    // the very next reload. Custom ranges (with `from`/`to`) always stay.
    if (
      range.preset !== 'custom' &&
      effectiveDefault &&
      range.preset === effectiveDefault
    ) {
      current.delete('range');
      current.delete('from');
      current.delete('to');
    }
    // Drop empties to keep URLs short.
    Array.from(current.entries()).forEach(([k, v]) => {
      if (v === '' || v == null) current.delete(k);
    });
    const serialized = current.toString();
    if (serialized === lastWrittenRef.current) return;
    if (serialized === stripDefault(location.search)) {
      lastWrittenRef.current = serialized;
      stampUrl(serialized);
      return;
    }
    lastWrittenRef.current = serialized;
    stampUrl(serialized);
    navigate({ pathname: location.pathname, search: serialized ? `?${serialized}` : '' }, { replace: true });
    // We intentionally exclude `location` & `navigate` from deps to avoid
    // the URL → store → URL loop. The hydrate effect handles back/forward.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.from, range.to, range.preset, storeKey, compareMode, timeRangeMode, savedDefault]);

  return <>{children}</>;
}

function stripDefault(search: string): string {
  const p = new URLSearchParams(search.replace(/^\?/, ''));
  return p.toString();
}
